import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {redirect} from "@khan/urls";
import type {Child} from "@khan/user-settings";

const strings = {
    childSettingsLabel: (nickname?: string | null) =>
        t`Settings for ${nickname}`,
} as const;

type Props = {
    managedChildren: Array<Child> | null | undefined;
    unmanagedChildren: Array<Child> | null | undefined;
};

export default class ChildrenSection extends React.Component<Props> {
    render(): React.ReactNode {
        const {managedChildren, unmanagedChildren} = this.props;
        return (
            // ID is for anchoring section in some post-login urls
            <View
                id="child-accounts"
                role="region"
                aria-label={t`Child accounts settings`}
            >
                <HeadingSmall style={styles.header} tag="h2">
                    {t`Child accounts`}
                </HeadingSmall>
                <Strut size={spacing.medium_16} />
                {managedChildren &&
                    !!managedChildren.length &&
                    managedChildren.map((child, index) => (
                        <React.Fragment key={`managed-child-${index}`}>
                            <View style={styles.row}>
                                <View style={[styles.row, styles.alignLabels]}>
                                    <LabelMedium
                                        style={[
                                            styles.verticalCenter,
                                            styles.label,
                                            styles.overflowHidden,
                                        ]}
                                    >
                                        {child.nickname || child.username}
                                    </LabelMedium>
                                </View>
                                <Strut size={spacing.medium_16} />
                                <Button
                                    href={`?kaid=${child.id}&ref=settings`}
                                    aria-label={strings.childSettingsLabel(
                                        child.nickname || child.username,
                                    )}
                                    onClick={() => {
                                        // NOTE(jeresig): We intentionally use a redirect here
                                        // as we need to force a full page reload for the settings page
                                        // to display correctly.
                                        redirect(
                                            `?kaid=${child.id}&ref=settings`,
                                        );
                                    }}
                                >
                                    {t`Settings`}
                                </Button>
                            </View>
                            <Strut size={spacing.small_12} />
                        </React.Fragment>
                    ))}
                {unmanagedChildren &&
                    !!unmanagedChildren.length &&
                    unmanagedChildren.map((child, index) => (
                        <React.Fragment key={`unmanaged-child-${index}`}>
                            <View style={styles.row}>
                                <LabelMedium
                                    style={[
                                        styles.verticalCenter,
                                        styles.label,
                                    ]}
                                >
                                    {child.nickname || child.username}
                                </LabelMedium>
                            </View>
                            <Strut size={spacing.small_12} />
                        </React.Fragment>
                    ))}
                <Strut size={spacing.small_12} />
                <Body>
                    <Trans>
                        <Link href="/settings/email">
                            Subscribe to weekly email reports
                        </Link>{" "}
                        about your children.
                    </Trans>
                </Body>
                <Strut size={spacing.small_12} />
                <Body>
                    <Trans>
                        You can also{" "}
                        <Link href="/teacher/students">
                            organize class lists
                        </Link>{" "}
                        or{" "}
                        <Link href="/parent">create a new child account</Link>.
                    </Trans>
                </Body>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    row: {
        flexDirection: "row",
    },
    label: {
        color: color.offBlack,
    },
    alignLabels: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 200,
        wordWrap: "break-word",
    },
    overflowHidden: {
        overflow: "hidden",
    },
});
